import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSnapshotsPaginated, exportSnapshotsCSV, exportSnapshotsExcel, downloadBlob } from '../api/monitoring';
import { useMonitoringProjects } from '../hooks/useMonitoringProjects';
import { useToast } from '../hooks/useToast';
import { FiChevronUp, FiChevronDown, FiSearch, FiDownload, FiRefreshCw, FiClock, FiFileText, FiEdit3, FiRotateCcw } from 'react-icons/fi';
import type { PaginatedSnapshotsResponse } from '../api/types';
import styles from './SnapshotsTab.module.css';
import { ExpandableValue } from '../components/MonitoringProject/ExpandableValue';

import { logger } from '../utils/logger';

type ContextType = { refreshTrigger: boolean };

type SortField = 'ts' | 'url' | 'status_initial' | 'status_final' | 'status_code' | 'title' | 'importance_score' | 'importance_tier' | 'canonical' | 'robots';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

export default function SnapshotsTab() {
  const { id } = useParams<{id:string}>();
  const { /* refreshTrigger */ } = useOutletContext<ContextType>(); // refreshTrigger not used
  const navigate = useNavigate();
  const toast = useToast();
  const { setPageTier, clearPageTier, isSettingTier, isClearingTier } = useMonitoringProjects();

  // State for filtering and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [importanceFilter, setImportanceFilter] = useState<string>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'ts', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // State for tier management
  const [openTierDropdown, setOpenTierDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const [activeSnapshot, setActiveSnapshot] = useState<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tierMenuRef = useRef<HTMLDivElement>(null);
  const tierButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  // Server-side paginated data fetching with sorting
  const { data, isLoading, isError, refetch } = useQuery<PaginatedSnapshotsResponse>({
    queryKey: ['snapshots', id, currentPage, pageSize, sortConfig.field, sortConfig.direction],
    queryFn: () => getSnapshotsPaginated(id!, currentPage, pageSize, sortConfig.field, sortConfig.direction),
    refetchInterval: false,
    refetchOnWindowFocus: true,
    enabled: !!id,
  });

  // Extract data from paginated response
  const snapshots = data?.items || [];
  const totalItems = data?.total || 0;
  const totalPages = data?.total_pages || 1;

  useLayoutEffect(() => {
    if (!openTierDropdown || !tierMenuRef.current || !tierButtonRef.current) return;
    const anchor = tierButtonRef.current.getBoundingClientRect();
    const menu = tierMenuRef.current.getBoundingClientRect();
    const gutter = 8;
    const below = anchor.bottom + 4;
    const top = below + menu.height <= window.innerHeight - gutter
      ? below
      : Math.max(gutter, anchor.top - menu.height - 4);
    const left = Math.max(gutter, Math.min(anchor.left, window.innerWidth - menu.width - gutter));
    setDropdownPosition({ top, left });
  }, [openTierDropdown]);

  useEffect(() => {
    if (!openTierDropdown) return;
    const close = () => {
      setOpenTierDropdown(null);
      setDropdownPosition(null);
      setActiveSnapshot(null);
    };
    const onScroll = (event: Event) => {
      if (event.target instanceof Node && tierMenuRef.current?.contains(event.target)) return;
      // Refetching a row can adjust the document scroll after a tier change.
      // Keep a newly opened menu attached to its visible button in that case.
      const anchor = tierButtonRef.current?.getBoundingClientRect();
      const menu = tierMenuRef.current?.getBoundingClientRect();
      if (!anchor || !menu || anchor.bottom <= 0 || anchor.top >= window.innerHeight || anchor.right <= 0 || anchor.left >= window.innerWidth) {
        close();
        return;
      }
      const gutter = 8;
      const below = anchor.bottom + 4;
      setDropdownPosition({
        top: below + menu.height <= window.innerHeight - gutter ? below : Math.max(gutter, anchor.top - menu.height - 4),
        left: Math.max(gutter, Math.min(anchor.left, window.innerWidth - menu.width - gutter)),
      });
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        tierButtonRef.current?.focus();
      }
    };
    window.addEventListener('resize', close);
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openTierDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside both the button and the dropdown menu
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        !target.closest(`.${styles.tierDropdownMenu}`)
      ) {
        setOpenTierDropdown(null);
        setDropdownPosition(null);
        setActiveSnapshot(null);
      }
    };

    if (openTierDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openTierDropdown]);

  // Handle tier change
  const handleTierChange = async (tier: 'A' | 'B' | 'C') => {
    if (!id || !activeSnapshot) return;

    if (!activeSnapshot.page_id) {
      toast.error('Backend error', 'page_id missing from snapshot response.');
      return;
    }

    logger.log('🎯 Attempting to set tier:', { projectId: id, pageId: activeSnapshot.page_id, tier });

    try {
      await setPageTier(id, activeSnapshot.page_id, tier);
      logger.log('✅ Tier set successfully');
      setOpenTierDropdown(null);
      setDropdownPosition(null);
      setActiveSnapshot(null);
      refetch();
    } catch (error: any) {
      logger.error('❌ Failed to set tier:', error);
      const errorMsg = error?.message || 'Unknown error';
      toast.error('Failed to update tier', errorMsg);
    }
  };

  // Handle tier reset (clear manual override)
  const handleTierReset = async () => {
    if (!id || !activeSnapshot) return;

    if (!activeSnapshot.page_id) {
      toast.error('Backend error', 'page_id missing from snapshot response.');
      return;
    }

    logger.log('🔄 Attempting to reset tier:', { projectId: id, pageId: activeSnapshot.page_id });

    try {
      await clearPageTier(id, activeSnapshot.page_id);
      logger.log('✅ Tier reset successfully');
      setOpenTierDropdown(null);
      setDropdownPosition(null);
      setActiveSnapshot(null);
      refetch();
    } catch (error: any) {
      logger.error('❌ Failed to reset tier:', error);
      const errorMsg = error?.message || 'Unknown error';
      toast.error('Failed to reset tier', errorMsg);
    }
  };

  // Client-side filtering on current page data (search and status filters)
  const filteredData = snapshots.filter(snapshot => {
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        snapshot.url.toLowerCase().includes(term) ||
        snapshot.title?.toLowerCase().includes(term) ||
        snapshot.description?.toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      // Backend sends status_code_final, but type def has status_final
      const statusCode = (snapshot as any).status_code_final ?? snapshot.status_final ?? snapshot.status_code ?? 0;
      if (statusFilter === 'errors' && statusCode < 400) return false;
      if (statusFilter === 'success' && (statusCode < 200 || statusCode >= 300)) return false;
      if (statusFilter === 'redirects') {
        // Backend sends status_code_initial and status_code_final
        const initialStatus = (snapshot as any).status_code_initial ?? snapshot.status_initial ?? snapshot.status_code ?? 0;
        const finalStatus = (snapshot as any).status_code_final ?? snapshot.status_final ?? snapshot.status_code ?? 0;
        if (initialStatus === finalStatus) return false;
      }
    }

    // Apply importance tier filter
    if (importanceFilter !== 'all') {
      if (!snapshot.importance_tier || snapshot.importance_tier !== importanceFilter) return false;
    }

    return true;
  });

  // Client-side sorting for special cases (canonical, robots) that need custom logic
  // For other fields, backend handles sorting
  const sortedData = (() => {
    const { field, direction } = sortConfig;

    // Only apply client-side sorting for canonical and robots (custom sorting logic)
    if (field === 'canonical' || field === 'robots') {
      return [...filteredData].sort((a, b) => {
        let aValue: number;
        let bValue: number;

        if (field === 'canonical') {
          // Sort order: empty canonicals first, then mismatched (red), then matching
          const aEmpty = !a.canonical;
          const bEmpty = !b.canonical;
          const aMismatch = a.canonical && a.canonical !== a.url;
          const bMismatch = b.canonical && b.canonical !== b.url;

          if (aEmpty && !bEmpty) return -1;
          if (!aEmpty && bEmpty) return 1;
          if (aMismatch && !bMismatch) return -1;
          if (!aMismatch && bMismatch) return 1;

          // Both same category, sort alphabetically
          aValue = (a.canonical || '').toLowerCase().charCodeAt(0);
          bValue = (b.canonical || '').toLowerCase().charCodeAt(0);
        } else if (field === 'robots') {
          // Sort order: empty tags first, then mismatched (noindex/red), then correct (index)
          const aEmpty = !a.robots;
          const bEmpty = !b.robots;
          const aNoIndex = a.robots && (a.robots.toLowerCase().includes('noindex') || a.robots.toLowerCase().includes('no-index'));
          const bNoIndex = b.robots && (b.robots.toLowerCase().includes('noindex') || b.robots.toLowerCase().includes('no-index'));

          if (aEmpty && !bEmpty) return -1;
          if (!aEmpty && bEmpty) return 1;
          if (aNoIndex && !bNoIndex) return -1;
          if (!aNoIndex && bNoIndex) return 1;

          // Both same category, sort alphabetically
          aValue = (a.robots || '').toLowerCase().charCodeAt(0);
          bValue = (b.robots || '').toLowerCase().charCodeAt(0);
        } else {
          return 0;
        }

        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    // For all other fields, use server-sorted data (no client-side sorting)
    return filteredData;
  })();

  // Reset to first page when page size changes
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  // Server-side Excel export (recommended)
  const handleExportExcel = async () => {
    if (!id || isExportingExcel) return;

    setIsExportingExcel(true);
    try {
      const blob = await exportSnapshotsExcel(id);
      const filename = `snapshots-${id}-${new Date().toISOString().split('T')[0]}.xlsx`;
      downloadBlob(blob, filename);
    } catch (error) {
      logger.error('Excel export failed:', error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  // Server-side CSV export
  const handleExportCSV = async () => {
    if (!id || isExportingCSV) return;

    setIsExportingCSV(true);
    try {
      const blob = await exportSnapshotsCSV(id);
      const filename = `snapshots-${id}-${new Date().toISOString().split('T')[0]}.csv`;
      downloadBlob(blob, filename);
    } catch (error) {
      logger.error('CSV export failed:', error);
    } finally {
      setIsExportingCSV(false);
    }
  };

  // Navigate to URL history page
  const handleViewURLHistory = (url: string) => {
    const encodedUrl = encodeURIComponent(url);
    navigate(`/monitoring/${id}/url-history?url=${encodedUrl}`);
  };

  const getStatusColor = (status: number | undefined) => {
    const statusCode = status ?? 0;
    if (statusCode >= 200 && statusCode < 300) return 'success';
    if (statusCode >= 300 && statusCode < 400) return 'warning';
    if (statusCode >= 400) return 'error';
    return 'neutral';
  };

  const getImportanceTierColor = (tier: string | null | undefined) => {
    if (tier === 'A') return 'tierA'; // Critical - red
    if (tier === 'B') return 'tierB'; // Important - yellow
    if (tier === 'C') return 'tierC'; // Standard - gray
    return 'neutral';
  };

  const getImportanceScoreColor = (score: number | null | undefined) => {
    if (!score) return 'neutral';
    if (score >= 80) return 'scoreHigh';    // 80-100: High importance
    if (score >= 50) return 'scoreMedium';  // 50-79: Medium importance
    return 'scoreLow';                      // 0-49: Low importance
  };

  // Check if canonical tag matches the URL exactly
  const isCanonicalMismatch = (url: string, canonical: string | null) => {
    if (!canonical) return false;
    return canonical !== url;
  };

  // Check if robots meta tag contains no-index
  const isRobotsNoIndex = (robots: string | null) => {
    if (!robots) return false;
    const robotsLower = robots.toLowerCase();
    return robotsLower.includes('noindex') || robotsLower.includes('no-index');
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortConfig.field !== field) return null;
    return sortConfig.direction === 'asc' ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />;
  };

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <FiRefreshCw className={styles.loadingIcon} size={24} />
        <span>Loading snapshots...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.errorState}>
        <p>Error loading snapshots. Please try again.</p>
        <button onClick={() => refetch()} className={styles.retryButton}>
          <FiRefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  if (!snapshots.length) {
    return (
      <div className={styles.emptyState}>
        <p>No snapshots yet.</p>
        <p className={styles.emptyStateHint}>
          Trigger a crawl to start collecting snapshots of your website pages.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <FiSearch className={styles.searchIcon} size={16} />
          <input
            type="text"
            aria-label="Search snapshots on this page"
            placeholder="Search in current page..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <button
          type="button"
          className={styles.filterToggle}
          aria-expanded={filtersOpen}
          aria-controls={`snapshot-controls-${id}`}
          onClick={() => setFiltersOpen((open) => !open)}
        >
          Filters &amp; export
          {(statusFilter !== 'all' || importanceFilter !== 'all') && <span className={styles.filterCount}>{Number(statusFilter !== 'all') + Number(importanceFilter !== 'all')}</span>}
          <FiChevronDown size={14} aria-hidden="true" />
        </button>

        <div id={`snapshot-controls-${id}`} className={`${styles.advancedControls} ${filtersOpen ? styles.controlsOpen : ''}`}>
          <div className={styles.filters}>
            <select
              aria-label="HTTP status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">All Status</option>
              <option value="success">Success (2xx)</option>
              <option value="redirects">Redirects</option>
              <option value="errors">Errors (4xx+)</option>
            </select>

            <select
              aria-label="Importance tier"
              value={importanceFilter}
              onChange={(e) => setImportanceFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">All Importance</option>
              <option value="A">🔴 Tier A (Critical)</option>
              <option value="B">🟡 Tier B (Important)</option>
              <option value="C">⚪ Tier C (Standard)</option>
            </select>

            <select
              aria-label="Snapshots per page"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className={styles.filterSelect}
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={200}>200 per page</option>
            </select>
          </div>

          <div className={styles.actions}>
            <button
              onClick={handleExportExcel}
              className={`${styles.exportButton} ${styles.exportExcel}`}
              disabled={isExportingExcel}
              title="Export to Excel (recommended)"
            >
              {isExportingExcel ? <FiRefreshCw size={16} className={styles.spinIcon} /> : <FiDownload size={16} />}
              {isExportingExcel ? 'Exporting...' : 'Excel'}
            </button>
            <button
              onClick={handleExportCSV}
              className={styles.exportButton}
              disabled={isExportingCSV}
              title="Export to CSV"
            >
              {isExportingCSV ? <FiRefreshCw size={16} className={styles.spinIcon} /> : <FiFileText size={16} />}
              {isExportingCSV ? 'Exporting...' : 'CSV'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.stats} aria-live="polite">
        <span className={styles.statItem}>
          {searchTerm || statusFilter !== 'all' || importanceFilter !== 'all'
            ? `${sortedData.length} of ${snapshots.length} on this page (${totalItems.toLocaleString()} total)`
            : `${totalItems.toLocaleString()} snapshots`}
        </span>
        <span className={styles.scrollHint}>Scroll table for more columns</span>
      </div>

      {/* Table */}
      <div className={styles.tableContainer} role="region" aria-label="Snapshots table" tabIndex={0}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th className={styles.headerCell} onClick={() => handleSort('ts')}>
                <div className={styles.headerContent}>
                  Date
                  <SortIcon field="ts" />
                </div>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('url')}>
                <div className={styles.headerContent}>
                  URL
                  <SortIcon field="url" />
                </div>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('status_initial')}>
                <div className={styles.headerContent}>
                  Initial Status
                  <SortIcon field="status_initial" />
                </div>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('status_code')}>
                <div className={styles.headerContent}>
                  Final Status
                  <SortIcon field="status_code" />
                </div>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('importance_tier')}>
                <div className={styles.headerContent}>
                  Tier
                  <SortIcon field="importance_tier" />
                </div>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('importance_score')}>
                <div className={styles.headerContent}>
                  Score
                  <SortIcon field="importance_score" />
                </div>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('title')}>
                <div className={styles.headerContent}>
                  Title
                  <SortIcon field="title" />
                </div>
              </th>
              <th className={styles.headerCell}>Description</th>
              <th className={styles.headerCell}>H1</th>
              <th className={styles.headerCell} onClick={() => handleSort('canonical')}>
                <div className={styles.headerContent}>
                  Canonical
                  <SortIcon field="canonical" />
                </div>
              </th>
              <th className={styles.headerCell} onClick={() => handleSort('robots')}>
                <div className={styles.headerContent}>
                  Robots
                  <SortIcon field="robots" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((snapshot) => (
              <tr key={`${snapshot.url}-${snapshot.ts}`} className={styles.dataRow}>
                <td className={styles.dataCell}>
                  <div className={styles.dateCell}>
                    {new Date(snapshot.ts).toLocaleDateString()}
                    <span className={styles.timeText}>
                      {new Date(snapshot.ts).toLocaleTimeString()}
                    </span>
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.urlCell}>
                    <a
                      href={snapshot.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.urlLink}
                      title={snapshot.url}
                    >
                      {snapshot.url}
                    </a>
                    <button
                      onClick={() => handleViewURLHistory(snapshot.url)}
                      className={styles.historyButton}
                      title="View URL history & timeline"
                    >
                      <FiClock size={14} />
                      View History
                    </button>
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.statusCell}>
                    {(() => {
                      // Backend sends status_code_initial, but type def has status_initial
                      const initialStatus = (snapshot as any).status_code_initial ?? snapshot.status_initial;
                      return initialStatus ? (
                        <span className={`${styles.statusBadge} ${styles[getStatusColor(initialStatus)]}`}>
                          {initialStatus}
                        </span>
                      ) : (
                        <span className={styles.emptyValue}>—</span>
                      );
                    })()}
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.statusCell}>
                    {(() => {
                      // Backend sends status_code_final, but type def has status_final
                      const finalStatus = (snapshot as any).status_code_final ?? snapshot.status_final ?? snapshot.status_code;
                      return (
                        <span className={`${styles.statusBadge} ${styles[getStatusColor(finalStatus)]}`}>
                          {finalStatus}
                        </span>
                      );
                    })()}
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.tierCell}>
                    {snapshot.importance_tier ? (
                      snapshot.page_id ? (
                        <div className={styles.tierDropdownContainer} ref={openTierDropdown === snapshot.id ? dropdownRef : null}>
                          <button
                            className={`${styles.tierBadge} ${styles[getImportanceTierColor(snapshot.importance_tier)]} ${styles.tierBadgeButton}`}
                            aria-expanded={openTierDropdown === snapshot.id}
                            onClick={(e) => {
                              if (openTierDropdown === snapshot.id) {
                                setOpenTierDropdown(null);
                                setDropdownPosition(null);
                                setActiveSnapshot(null);
                              } else {
                                tierButtonRef.current = e.currentTarget;
                                const rect = e.currentTarget.getBoundingClientRect();
                                setDropdownPosition({
                                  top: rect.bottom + 4,
                                  left: rect.left
                                });
                                setOpenTierDropdown(snapshot.id);
                                setActiveSnapshot(snapshot);
                              }
                            }}
                            disabled={isSettingTier || isClearingTier}
                            title={snapshot.importance_tier_manual ? `Manual: Tier ${snapshot.importance_tier} (click to change)` : `Auto: Tier ${snapshot.importance_tier} (click to override)`}
                          >
                            {snapshot.importance_tier}
                            {snapshot.importance_tier_manual && <FiEdit3 size={10} className={styles.manualIcon} />}
                          </button>

                        {openTierDropdown === snapshot.id && dropdownPosition && createPortal(
                          <div
                            className={styles.tierDropdownMenu}
                            ref={tierMenuRef}
                            style={{
                              top: `${dropdownPosition.top}px`,
                              left: `${dropdownPosition.left}px`
                            }}
                          >
                            <div className={styles.tierDropdownHeader}>
                              {activeSnapshot?.importance_tier_manual ? 'Manual Override' : 'Set Manual Tier'}
                            </div>
                            <button
                              className={`${styles.tierDropdownItem} ${styles.tierA}`}
                              onClick={() => {
                                logger.log('🔴 TIER A CLICKED');
                                handleTierChange('A');
                              }}
                              disabled={isSettingTier}
                            >
                              <span className={styles.tierDropdownBadge}>A</span>
                              <span>High Priority</span>
                            </button>
                            <button
                              className={`${styles.tierDropdownItem} ${styles.tierB}`}
                              onClick={() => handleTierChange('B')}
                              disabled={isSettingTier}
                            >
                              <span className={styles.tierDropdownBadge}>B</span>
                              <span>Medium Priority</span>
                            </button>
                            <button
                              className={`${styles.tierDropdownItem} ${styles.tierC}`}
                              onClick={() => handleTierChange('C')}
                              disabled={isSettingTier}
                            >
                              <span className={styles.tierDropdownBadge}>C</span>
                              <span>Low Priority</span>
                            </button>
                            {activeSnapshot?.importance_tier_manual && (
                              <>
                                <div className={styles.tierDropdownDivider}></div>
                                <button
                                  className={`${styles.tierDropdownItem} ${styles.tierReset}`}
                                  onClick={() => handleTierReset()}
                                  disabled={isClearingTier}
                                >
                                  <FiRotateCcw size={14} />
                                  <span>Reset to Auto</span>
                                </button>
                              </>
                            )}
                          </div>,
                          document.body
                        )}
                      </div>
                      ) : (
                        <span className={`${styles.tierBadge} ${styles[getImportanceTierColor(snapshot.importance_tier)]}`} title="Backend missing page_id">
                          {snapshot.importance_tier}
                        </span>
                      )
                    ) : (
                      <span className={styles.emptyValue}>—</span>
                    )}
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.statusCell}>
                    {snapshot.importance_score !== null && snapshot.importance_score !== undefined ? (
                      <span className={`${styles.scoreBadge} ${styles[getImportanceScoreColor(snapshot.importance_score)]}`}>
                        {snapshot.importance_score}
                      </span>
                    ) : (
                      <span className={styles.emptyValue}>—</span>
                    )}
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.textCell} title={snapshot.title || ''}>
                    {snapshot.title ? <ExpandableValue value={snapshot.title} label="Title" /> : <span className={styles.emptyValue}>—</span>}
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.textCell} title={snapshot.description || ''}>
                    {snapshot.description ? <ExpandableValue value={snapshot.description} label="Description" /> : <span className={styles.emptyValue}>—</span>}
                  </div>
                </td>
                <td className={styles.dataCell}>
                  <div className={styles.textCell} title={snapshot.h1 || ''}>
                    {snapshot.h1 ? <ExpandableValue value={snapshot.h1} label="H1" /> : <span className={styles.emptyValue}>—</span>}
                  </div>
                </td>
                <td className={`${styles.dataCell} ${isCanonicalMismatch(snapshot.url, snapshot.canonical) ? styles.errorCell : ''}`}>
                  <div className={styles.textCell} title={snapshot.canonical || ''}>
                    {snapshot.canonical ? <ExpandableValue value={snapshot.canonical} label="Canonical" /> : <span className={styles.emptyValue}>—</span>}
                  </div>
                </td>
                <td className={`${styles.dataCell} ${isRobotsNoIndex(snapshot.robots) ? styles.errorCell : ''}`}>
                  <div className={styles.textCell} title={snapshot.robots || ''}>
                    {snapshot.robots ? <ExpandableValue value={snapshot.robots} label="Robots" /> : <span className={styles.emptyValue}>—</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className={styles.paginationButton}
          >
            Previous
          </button>
          
          <div className={styles.paginationInfo}>
            Page {currentPage} of {totalPages}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className={styles.paginationButton}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
